import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  isFavorite,
  isComplete,
  isVerified,
  hasItemsInList,
  itemsInListOfType,
  getListItemInList,
  isItemInList
} from '@alltrails/shared/utils/listHelpers';
import { PageStrings } from '@alltrails/shared/utils/pageStringHelpers';
import ReactDOM from 'react-dom';
import useThunkReducer from './useThunkReducer';
import listReducer, { toggleListItem, addCreatedList, LISTS, setListsAndListItems, updateListOrder } from '../ducks/lists';
import ListsModal from '../components/lists/ListsModal';
import { usersContentPrivacyPolicy } from 'utils/user_helpers';
import { LISTS as PRIVACY_POLICY_LISTS } from 'utils/privacy_policy_helpers';
import { modalSignup } from '../utils/modal_funnel_util';
import { SearchInsightsContext } from '../components/SearchInsightsProvider';

const SaveToListModal = lazy(() => import('@alltrails/shared/components/SaveToListModal'));

const useListItems = (initialState, context, page) => {
  const [showModal, toggleShowModal] = useState(false);
  const [activeItem, setActiveItem] = useState({ type: null, id: null, objectID: null });
  const [state, dispatch] = useThunkReducer(listReducer, LISTS, initialState);

  useEffect(() => {
    /**
     * this ensures that we lock the screen in the monolith so users
     * cant scroll while the modal is active
     *
     */
    const { classList } = document.querySelector('html');
    return showModal ? classList.add('modalOpen') : classList.remove('modalOpen');
  }, [showModal]);

  /**
   * handleModalFavoriteClick
   *
   * Triggers the request when a user adds a piece of content to a custom list.
   *
   * @param {number} listId
   *
   */
  const handleModalFavoriteClick = conversionTrackingFunction => {
    return listId => {
      dispatch(toggleListItem(context.currentUser.id, listId, activeItem.type, activeItem.ID, page));

      const isAlreadyInFavorites = isFavorite(activeItem.type, activeItem.ID, state.listItems);
      if (activeItem.objectID && !isAlreadyInFavorites) {
        conversionTrackingFunction(`${activeItem.objectID}`);
      }
    };
  };

  /**
   * handleModalListCreated
   *
   * adds a new list when a user creates one from the modal.
   */
  const handleModalListCreated = newList => {
    dispatch(addCreatedList(newList));
  };

  /**
   * closeModal
   *
   * closes the modal and resets the type and id
   */
  const closeModal = () => {
    toggleShowModal(false);
    setActiveItem({ type: null, id: null, objectID: null });
  };

  const renderModal = () => {
    if (!showModal) {
      return null;
    }

    if (context.featureFlags.flexiblePrivacy) {
      return ReactDOM.createPortal(
        <Suspense fallback={null}>
          <SearchInsightsContext.Consumer>
            {({ logConversionEvent }) => {
              return (
                <SaveToListModal
                  closeModal={closeModal}
                  type={activeItem.type}
                  ID={activeItem.ID}
                  lists={state.lists}
                  listItems={state.listItems}
                  handleModalFavoriteClick={handleModalFavoriteClick(logConversionEvent)}
                  handleModalListCreated={handleModalListCreated}
                  initialPrivacyPolicy={usersContentPrivacyPolicy(context, PRIVACY_POLICY_LISTS)}
                />
              );
            }}
          </SearchInsightsContext.Consumer>
        </Suspense>,
        document.getElementById('modalPortal')
      );
    }

    return ReactDOM.createPortal(
      <SearchInsightsContext.Consumer>
        {({ logConversionEvent }) => {
          return (
            <ListsModal
              closeModal={closeModal}
              type={activeItem.type}
              ID={activeItem.ID}
              lists={state.lists}
              listItems={state.listItems}
              handleModalFavoriteClick={handleModalFavoriteClick(logConversionEvent)}
              handleModalListCreated={handleModalListCreated}
            />
          );
        }}
      </SearchInsightsContext.Consumer>,
      document.getElementById('modalPortal')
    );
  };

  /**
   * handleFavoriteClick
   *
   * Used to trigger the modal and set the active item. After this is triggered, the modal should open
   * and there is an active item in which users can add items to a list.
   *
   * @param {string} type
   * @param {number} ID
   * @param {string} objectID
   *
   */
  const handleFavoriteClick = (type, ID, objectID = null) => {
    if (!context.currentUser) {
      return modalSignup(`${type}-resultcard-favorite`, window.location.pathname);
    }

    setActiveItem({ type, ID, objectID });
    toggleShowModal(true);
  };

  /**
   * handleToggleListItem
   *
   * This method is used to add or remove an item from a list without having to go through the modal flow.
   * Used currently in search_app to add and remove completed items, without use of the modal.
   *
   * @param {number} listId
   * @param {string} type
   * @param {number} objectId
   * @param {number} userId
   *
   */
  const handleToggleListItem = (listId, type, objectId, userId = null) => {
    if (!context.currentUser) {
      return modalSignup(`${type}-resultcard-favorite`, window.location.pathname);
    }

    dispatch(toggleListItem(userId || context.currentUser.id, listId, type, objectId, page));
  };

  /**
   * This hook returns a grouping of different methods to interract with a favorite list, that
   * pull from the list_helpers util to make a number of different calls.
   *
   * it also renders passes a rendered version of the ListModal in which a user can add different pieces of
   * content to their lists.
   *
   */

  return {
    listMethods: {
      handleFavoriteClick,
      isComplete: (type, id) => isComplete(type, id, state.belongsToCurrentUser ? state.userListItems : state.listItems),
      isFavorite: (type, id) => isFavorite(type, id, state.listItems),
      isVerified: (type, id) => isVerified(type, id, state.belongsToCurrentUser ? state.userListItems : state.listItems),
      hasItemsInList: (listId, listItems) => hasItemsInList(listId, listItems), // this method needs to have the listItems passed in
      toggleListItem: (listId, type, objectId, userId = null) => handleToggleListItem(listId, type, objectId, userId),
      itemsInListOfType: (listId, type) => itemsInListOfType(listId, type, state.listItems),
      getListItemInList: (listId, type, objectId, listItems) => getListItemInList(listId, type, objectId, listItems),
      showFavoriteHeart: page !== PageStrings.EXPLORE_COMPLETED_PAGE,
      isItemInList: (type, id, listId) => isItemInList(type, id, state.listItems[listId]),
      setListsAndListItems: ({ lists, listItems }) => dispatch(setListsAndListItems({ lists, listItems })),
      updateListOrder: (listItems, listId) => dispatch(updateListOrder(listItems, listId))
    },
    listItems: state.listItems,
    lists: state.lists,
    userListItems: state.userListItems,
    latestListAction: state.latestListAction,
    renderModal
  };
};

export default useListItems;
